import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { listPauses, memberPauseState, createPause, endPause, listAttendanceActiveCustomers } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Plus, Play, Smartphone, User, Tablet } from "lucide-react";

const fmt = (iso) => (iso ? new Date(iso.slice(0, 10) + "T00:00:00").toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—");
const addDays = (iso, n) => {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};
const REASONS = [["travel", "Travel"], ["illness", "Illness"], ["injury", "Injury"], ["other", "Other"]];
const VIA = { app: ["App", Smartphone], staff: ["Staff", User], tablet: ["Check-in", Tablet], planned: ["Planned end", Play] };
const detail = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return Array.isArray(d) ? d[0]?.msg : d || fallback;
};

export default function MembershipPauses() {
  const { activeFacility } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await listPauses());
    } catch (e) {
      toast.error(detail(e, "Couldn't load pauses"));
      setData({ current: [], recent: [], today: "" });
    }
  }, []);
  useEffect(() => { load(); }, [load, activeFacility?.id]);

  const end = async (p) => {
    const what = p.status === "scheduled" ? `Cancel ${p.client_name}'s pause from ${p.label_start}? No pause days are used.`
      : `Reactivate ${p.client_name} today? Back within the first 3 days cancels the pause (no days used).`;
    if (!window.confirm(what)) return;
    try {
      await endPause(p.id);
      toast.success(p.status === "scheduled" ? "Pause cancelled" : "Membership reactivated");
      load();
    } catch (e) {
      toast.error(detail(e, "Couldn't end the pause"));
    }
  };

  return (
    <div className="space-y-6" data-testid="membership-pauses">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="eyebrow">Member App · Members</div>
          <h2 className="font-display text-3xl font-extrabold mt-1 tracking-tight">Pauses</h2>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Until the member app is live, pause members here when they ask (Pause a member). Memberships sold in YoActiv pause too.
            Members get 1 pause day per programme week; each pause is at least 3 days (back within those 3 days cancels it, no days used)
            and every pause day moves the membership end date (gate access and renewal reminders follow it). Checking in at the front
            desk ends a pause. The 6 Week Challenge and memberships of 4 weeks or less can&apos;t be paused.
          </p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="pause-add"><Plus className="mr-1 h-4 w-4" /> Pause a member</Button>
      </div>

      {data === null ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>
      ) : data.current.length === 0 ? (
        <div className="card-surface p-10 text-center text-sm text-zinc-500">Nobody is paused at {activeFacility?.name || "this gym"} right now.</div>
      ) : (
        <div className="space-y-2" data-testid="pauses-current">
          {data.current.map((p) => <Row key={p.id} p={p} onEnd={() => end(p)} />)}
        </div>
      )}

      {data?.recent?.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Recently ended</div>
          <div className="space-y-2 opacity-70">{data.recent.map((p) => <Row key={p.id} p={p} />)}</div>
        </div>
      )}

      <PauseDialog open={open} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function Row({ p, onEnd }) {
  const [via, Icon] = VIA[p.created_via] || ["", User];
  const ended = VIA[p.ended_via]?.[0];
  return (
    <div className="card-surface flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`pause-${p.id}`}>
      <div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-zinc-100">{p.client_name || p.customer_key}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
            p.status === "active" ? "bg-amber-500/15 text-amber-300" : p.status === "scheduled" ? "bg-sky-500/15 text-sky-300" : "bg-zinc-700/40 text-zinc-400"}`}>
            {p.status === "active" ? "Paused" : p.status === "scheduled" ? "Booked" : p.status}
          </span>
          <span className="text-xs text-zinc-500">{p.programme} · {REASONS.find(([k]) => k === p.reason)?.[1]}</span>
        </div>
        <div className="text-sm text-zinc-400">
          {p.status === "cancelled" ? `From ${p.label_start} (cancelled)`
            : p.status === "ended" ? `From ${p.label_start}, back ${p.label_end} · ${p.days} days used`
            : p.open_ended ? `From ${p.label_start}, until they're back (up to ${p.label_end})` : `${p.label_start} to ${p.label_end} · ${p.days} days`}
        </div>
        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-zinc-500">
          <Icon className="h-3 w-3" /> {via}{p.created_by_name ? ` (${p.created_by_name})` : ""}{ended ? ` · ended by ${ended.toLowerCase()}` : ""}
        </div>
      </div>
      {onEnd && (
        <Button size="sm" variant="outline" onClick={onEnd}>{p.status === "scheduled" ? "Cancel" : "Reactivate"}</Button>
      )}
    </div>
  );
}

function PauseDialog({ open, onClose, onSaved }) {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [pick, setPick] = useState(null);
  const [state, setState] = useState(null);
  const [form, setForm] = useState({ start_date: "", end_date: "", reason: "travel" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPick(null); setState(null); setQ("");
    listAttendanceActiveCustomers().then(setMembers, () => setMembers([]));
  }, [open]);

  useEffect(() => {
    if (!pick) return;
    memberPauseState(pick.customer_key).then((s) => {
      setState(s);
      setForm({ start_date: s.today, end_date: addDays(s.today, Math.min(6, s.allowance.remaining - 1)), reason: "travel" });
    }, (e) => toast.error(detail(e, "Couldn't load the membership")));
  }, [pick]);

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    return members.filter((m) => (m.name || "").toLowerCase().includes(t) || (m.customer_key || "").includes(t)).slice(0, 8);
  }, [q, members]);

  const days = form.start_date && form.end_date ? Math.round((new Date(form.end_date) - new Date(form.start_date)) / 864e5) + 1 : null;
  const newEnd = state?.membership?.end_date && days ? addDays(state.membership.end_date, days) : null;

  const save = async () => {
    setSaving(true);
    try {
      await createPause({ customer_key: pick.customer_key, start_date: form.start_date, end_date: form.end_date || null, reason: form.reason });
      toast.success(`${pick.name} paused`);
      onSaved();
    } catch (e) {
      toast.error(detail(e, "Couldn't pause"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg bg-[#0f0f0f] border-[#262626] text-zinc-100">
        <DialogHeader>
          <DialogTitle>Pause a member</DialogTitle>
          <DialogDescription>Uses the member's pause allowance, the same as pausing in the app. Members on the app get a notification.</DialogDescription>
        </DialogHeader>
        {!pick ? (
          <div className="space-y-2">
            <Input autoFocus className="bg-[#0a0a0a] border-[#262626] text-zinc-100" placeholder="Search active members by name or phone" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pause-search" />
            {matches.map((m) => (
              <button key={m.customer_key} type="button" onClick={() => setPick(m)}
                className="flex w-full items-center justify-between rounded-md border border-zinc-800 px-3 py-2 text-left text-sm hover:border-zinc-600">
                <span className="text-zinc-100">{m.name}</span><span className="text-xs text-zinc-500">{m.programme}</span>
              </button>
            ))}
          </div>
        ) : !state ? (
          <div className="py-6 text-center text-sm text-zinc-500">Loading…</div>
        ) : state.pause ? (
          <div className="text-sm text-zinc-300">{pick.name} already has a pause from {state.pause.label_start}. End it from the list first.</div>
        ) : !state.allowance.pausable || state.allowance.remaining < state.min_days ? (
          <div className="text-sm text-zinc-300">
            {!state.allowance.pausable ? `${state.membership?.programme || "This programme"} can't be paused.`
              : `${pick.name} has ${state.allowance.remaining} pause days left; a pause needs at least ${state.min_days}.`}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-sm text-zinc-300">
              <span className="font-semibold text-zinc-100">{pick.name}</span> · {state.membership?.programme} · {state.allowance.remaining} of {state.allowance.total} pause days left
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="text-xs text-zinc-400 space-y-1 block">First day
                <Input type="date" className="bg-[#0a0a0a] border-[#262626] text-zinc-100" value={form.start_date} min={state.today} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </label>
              <label className="text-xs text-zinc-400 space-y-1 block">Last day (empty = until they're back)
                <Input type="date" className="bg-[#0a0a0a] border-[#262626] text-zinc-100" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
              </label>
            </div>
            <div className="flex gap-2">
              {REASONS.map(([k, l]) => (
                <button key={k} type="button" onClick={() => setForm({ ...form, reason: k })}
                  className={`rounded-md border px-3 py-1.5 text-xs ${form.reason === k ? "border-emerald-500/60 text-zinc-100" : "border-zinc-800 text-zinc-400"}`}>{l}</button>
              ))}
            </div>
            <div className="rounded-lg border border-zinc-800 bg-[#0b0b0b] p-3 text-sm text-zinc-300">
              {days ? <>{days} days. </> : <>Until they're back (holds up to {state.allowance.remaining} days). </>}
              End date {fmt(state.membership?.end_date)} → <span className="text-zinc-100">{newEnd ? fmt(newEnd) : `up to ${fmt(addDays(state.membership.end_date, state.allowance.remaining))}`}</span>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setPick(null)}>Back</Button>
              <Button onClick={save} disabled={saving} data-testid="pause-save">{saving ? "Pausing…" : "Pause membership"}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
